<template>
  <div class="contract-print-view">
    <!-- ============================================
         ESTADO: LOADING
         ============================================ -->
    <div v-if="loading" class="contract-loading">
      <div class="contract-loading__spinner"></div>
      <p class="contract-loading__text">Carregando contrato...</p>
    </div>

    <!-- ============================================
         ESTADO: CONTRATO NÃO ENCONTRADO
         ============================================ -->
    <div v-else-if="!contract" class="contract-not-found">
      <div class="contract-not-found__icon">
        <i class="fas fa-file-excel"></i>
      </div>
      <h2 class="contract-not-found__title">Contrato não encontrado</h2>
      <p class="contract-not-found__description">
        O contrato solicitado não existe ou foi removido.
      </p>
    </div>

    <!-- ============================================
         ESTADO: CONTRATO CARREGADO
         ============================================ -->
    <div v-else class="contract-container">
      <!-- Cabeçalho com Logo e Ações -->
      <header class="contract-header">
        <div class="contract-header__brand" v-if="contract.rest_home_logo_url">
          <img
            :src="contract.rest_home_logo_url"
            :alt="`Logo ${contract.rest_home_fantasy_name}`"
            class="contract-header__logo"
          />
          <div class="contract-header__company-info">
            <h2 class="contract-header__company-name">
              {{ contract.rest_home_fantasy_name }}
            </h2>
            <span class="contract-header__document-type">
              <i class="fas fa-file-contract"></i>
              Documento
            </span>
          </div>
        </div>
      </header>

      <!-- Corpo do Contrato -->
      <main class="contract-body">
        <!-- Título do Contrato -->
        <div class="contract-title-section">
          <h1 class="contract-title">{{ name }}</h1>
          <div class="contract-title-divider"></div>
        </div>

        <!-- Conteúdo do Contrato -->
        <article class="contract-content">
          <div
            class="contract-text"
            v-html="formatContract(replaced_text)"
          ></div>
        </article>

        <!-- Rodapé com Ações -->
        <footer class="contract-footer">
          <div class="contract-footer__info">
            <i class="fas fa-info-circle"></i>
            <span>
              Este documento é gerado automaticamente pelo sistema. Verifique
              todas as informações antes de imprimir.
            </span>
          </div>
          <div class="contract-footer__actions">
            <button
              class="contract-btn contract-btn--primary contract-btn--large"
              @click="handleToPrint"
            >
              <i class="fas fa-print"></i>
              <span>Imprimir</span>
            </button>
          </div>
        </footer>
      </main>
    </div>

    <!-- ============================================
         SEÇÃO DE IMPRESSÃO (Oculta na tela)
         ============================================ -->
    <div id="print-section" class="print-section">
      <!-- Cabeçalho de Impressão -->
      <div class="print-header" v-if="contract && contract.rest_home_logo_url">
        <img
          :src="contract.rest_home_logo_url"
          :alt="`Logo ${contract.rest_home_fantasy_name}`"
          class="print-header__logo"
        />
        <div class="print-header__info">
          <p class="print-header__company">
            {{ contract.rest_home_fantasy_name }}
          </p>
        </div>
      </div>

      <!-- Título de Impressão -->
      <div class="print-contract-title">
        {{ name }}
      </div>

      <!-- Conteúdo de Impressão -->
      <div class="print-contract-content">
        <div v-html="formatContract(replaced_text)"></div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";

export default {
  name: "ContractPrintView",

  data() {
    return {
      loading: false,
      contract_id: null,
      resident_id: null,
      contract: null,
      name: "",
      replaced_text: "",
    };
  },

  async created() {
    if (this.$route.params.contract_id) {
      this.contract_id = this.$route.params.contract_id;
    }

    if (this.$route.params.resident_id) {
      this.resident_id = this.$route.params.resident_id;
    }

    await this.getContract();
  },

  methods: {
    async getContract() {
      try {
        this.loading = true;

        const { contract_id, resident_id } = this;
        const params = { contract_id, resident_id };

        const response = await api.get("/contract", { params });
        const { error, contract, replaced_text } = response.data;

        if (!error) {
          this.contract = contract;
          this.name = contract.name;
          this.replaced_text = replaced_text;
        }
      } catch (error) {
        console.error("Erro ao carregar contrato:", error);
      } finally {
        this.loading = false;
      }
    },

    formatContract(text) {
      if (!text) return "";
      return text.replace(/\n/g, "<br>");
    },

    handleToPrint() {
      window.print();
    },
  },
};
</script>

<style scoped>
/* ============================================
   VARIÁVEIS CSS LOCAIS
   ============================================ */
.contract-print-view {
  --ct-primary: #33495e;
  --ct-primary-light: #4a6785;
  --ct-primary-dark: #243342;
  --ct-accent: #3b82f6;
  --ct-accent-light: #dbeafe;
  --ct-accent-dark: #2563eb;
  --ct-success: #10b981;
  --ct-warning: #f59e0b;
  --ct-danger: #ef4444;
  --ct-neutral: #6b7280;
  --ct-neutral-light: #f3f4f6;
  --ct-bg: #f8fafc;
  --ct-bg-paper: #ffffff;
  --ct-border: #e5e7eb;
  --ct-text: #1f2937;
  --ct-text-muted: #6b7280;
  --ct-text-light: #9ca3af;
  --ct-radius: 12px;
  --ct-radius-sm: 8px;
  --ct-radius-lg: 16px;
  --ct-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  --ct-shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06);
  --ct-shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
  --ct-transition: 0.2s ease;

  min-height: 100vh;
  background: var(--ct-bg);
}

/* ============================================
   ESTADO: LOADING
   ============================================ */
.contract-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 1.5rem;
}

.contract-loading__spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--ct-border);
  border-top-color: var(--ct-accent);
  border-radius: 50%;
  animation: ct-spin 1s linear infinite;
}

@keyframes ct-spin {
  to {
    transform: rotate(360deg);
  }
}

.contract-loading__text {
  font-size: 1rem;
  color: var(--ct-text-muted);
  margin: 0;
}

/* ============================================
   ESTADO: NÃO ENCONTRADO
   ============================================ */
.contract-not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
  text-align: center;
}

.contract-not-found__icon {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ct-danger);
  background: linear-gradient(135deg, #fecaca 0%, #fca5a5 100%);
  border-radius: 50%;
  margin-bottom: 1.5rem;
}

.contract-not-found__icon i {
  font-size: 2.5rem;
  color: var(--ct-danger);
}

.contract-not-found__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ct-text);
  margin: 0 0 0.5rem;
}

.contract-not-found__description {
  font-size: 1rem;
  color: var(--ct-text-muted);
  margin: 0 0 1.5rem;
  max-width: 400px;
}

.contract-not-found__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--ct-primary);
  color: #fff;
  border: none;
  border-radius: var(--ct-radius-sm);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--ct-transition);
}

.contract-not-found__btn:hover {
  background: var(--ct-primary-light);
  transform: translateY(-2px);
}

/* ============================================
   CONTAINER PRINCIPAL
   ============================================ */
.contract-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem;
}

/* ============================================
   CABEÇALHO
   ============================================ */
.contract-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.25rem 1.5rem;
  background: var(--ct-bg-paper);
  border-radius: var(--ct-radius-lg);
  box-shadow: var(--ct-shadow);
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.contract-header__brand {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.contract-header__logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  border-radius: var(--ct-radius-sm);
  border: 1px solid var(--ct-border);
  padding: 0.25rem;
  background: #fff;
}

.contract-header__company-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.contract-header__company-name {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--ct-text);
  margin: 0;
}

.contract-header__document-type {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--ct-accent);
  font-weight: 500;
}

.contract-header__document-type i {
  font-size: 0.75rem;
}

.contract-header__actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* ============================================
   BOTÕES
   ============================================ */
.contract-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-radius: var(--ct-radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--ct-transition);
  border: none;
  white-space: nowrap;
}

.contract-btn--primary {
  background: linear-gradient(
    135deg,
    var(--ct-accent) 0%,
    var(--ct-accent-dark) 100%
  );
  color: #fff;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.contract-btn--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.contract-btn--secondary {
  background: var(--ct-neutral-light);
  color: var(--ct-text);
}

.contract-btn--secondary:hover {
  background: var(--ct-border);
}

.contract-btn--outline {
  background: transparent;
  color: var(--ct-text-muted);
  border: 1px solid var(--ct-border);
}

.contract-btn--outline:hover {
  background: var(--ct-neutral-light);
  color: var(--ct-text);
}

.contract-btn--large {
  padding: 0.85rem 1.75rem;
  font-size: 1rem;
}

/* ============================================
   CORPO DO CONTRATO
   ============================================ */
.contract-body {
  background: var(--ct-bg-paper);
  border-radius: var(--ct-radius-lg);
  box-shadow: var(--ct-shadow-md);
  overflow: hidden;
}

/* Título */
.contract-title-section {
  padding: 2rem 2.5rem 1.5rem;
  text-align: center;
  background: linear-gradient(
    to bottom,
    var(--ct-neutral-light) 0%,
    var(--ct-bg-paper) 100%
  );
}

.contract-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ct-primary);
  margin: 0 0 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contract-title-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(
    90deg,
    var(--ct-accent) 0%,
    var(--ct-accent-dark) 100%
  );
  border-radius: 2px;
  margin: 0 auto;
}

/* Conteúdo */
.contract-content {
  padding: 1.5rem 2.5rem 2rem;
}

.contract-text {
  font-size: 1rem;
  line-height: 1.8;
  color: var(--ct-text);
  text-align: justify;
}

.contract-text >>> p {
  margin-bottom: 1rem;
}

.contract-text >>> strong,
.contract-text >>> b {
  color: var(--ct-primary);
}

/* ============================================
   RODAPÉ
   ============================================ */
.contract-footer {
  padding: 1.5rem 2.5rem;
  border-top: 1px solid var(--ct-border);
  background: var(--ct-neutral-light);
}

.contract-footer__info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--ct-accent-light);
  border-radius: var(--ct-radius-sm);
  margin-bottom: 1.5rem;
  font-size: 0.85rem;
  color: var(--ct-accent-dark);
  line-height: 1.5;
}

.contract-footer__info i {
  font-size: 1rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.contract-footer__actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
}

/* ============================================
   SEÇÃO DE IMPRESSÃO
   ============================================ */
#print-section {
  display: none;
}

@media print {
  /* Ocultar tudo exceto a seção de impressão */
  body * {
    visibility: hidden;
  }

  #print-section,
  #print-section * {
    visibility: visible !important;
  }

  #print-section {
    display: block !important;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    min-height: 100%;
    background: white !important;
    padding: 0;
    margin: 0;
    z-index: 9999;
  }

  @page {
    margin: 15mm 20mm;
    size: A4;
  }

  /* Cabeçalho de impressão */
  .print-header {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .print-header__logo {
    width: 60px;
    height: 60px;
    object-fit: contain;
  }

  .print-header__company {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  /* Título de impressão */
  .print-contract-title {
    text-align: center;
    font-size: 1.25rem;
    font-weight: 700;
    color: #000;
    text-transform: uppercase;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #ccc;
  }

  .print-section {
    page-break-inside: avoid;
  }

  /* Ocultar botões na impressão */
  .contract-btn,
  .contract-header__actions,
  .contract-footer__actions {
    display: none !important;
  }
}

/* ============================================
   RESPONSIVIDADE
   ============================================ */
@media (max-width: 768px) {
  .contract-container {
    padding: 1rem;
  }

  .contract-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
  }

  .contract-header__actions {
    width: 100%;
    justify-content: stretch;
  }

  .contract-header__actions .contract-btn {
    flex: 1;
    justify-content: center;
  }

  .contract-title-section {
    padding: 1.5rem 1.25rem 1rem;
  }

  .contract-title {
    font-size: 1.25rem;
  }

  .contract-content {
    padding: 1rem 1.25rem 1.5rem;
  }

  .contract-text {
    font-size: 0.95rem;
    text-align: left;
  }

  .contract-footer {
    padding: 1rem 1.25rem;
  }

  .contract-footer__actions {
    flex-direction: column;
  }

  .contract-footer__actions .contract-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .contract-header__brand {
    flex-direction: column;
    align-items: flex-start;
  }

  .contract-header__logo {
    width: 48px;
    height: 48px;
  }

  .contract-title {
    font-size: 1.1rem;
  }
}
</style>